(() => {
  // ---------------------------------------------------------------------
  // AAS-5267: fast-click gate for every [data-wishlist-toggle] heart.
  //
  // The actual add/remove lives in Wishlist King's admin-owned theme code
  // (cdn.appmate.io/themecode/.../wishlist-button-headless.js), whose click
  // handler branches on async productInfo.inWishlist. Clicks landing while
  // the previous API call is still settling are either silently swallowed
  // by the app or routed down the wrong branch (a duplicate add the app's
  // cache dedupes into a no-op, or a remove with wishlistItemId still
  // undefined) — on a slow connection the heart then reads as frozen.
  //
  // The gate lets ONE click through per button, then swallows clicks until
  // the button's observable state (is-wishlisted / aria-pressed, flipped by
  // the app's updated()) actually changes, or a settle timeout passes.
  // Registered before Wishlist King's host-level handler (document capture
  // beats element listeners) AND before datalayer-tracking.js registers its
  // own capture listener (theme.liquid loads this file first), so
  // stopImmediatePropagation keeps swallowed clicks out of GA4 too — no
  // phantom add_to_wishlist, no toast, no app call.
  // ---------------------------------------------------------------------
  const TOGGLE_SETTLE_MS = 2500;
  const toggleGate = new WeakMap();

  const pressedState = (btn) =>
    String(btn.classList.contains('is-wishlisted') || btn.getAttribute('aria-pressed') === 'true');

  const releaseGate = (btn) => {
    const gate = toggleGate.get(btn);
    if (!gate) return;
    clearTimeout(gate.timer);
    gate.observer.disconnect();
    toggleGate.delete(btn);
  };

  document.addEventListener(
    'click',
    (event) => {
      const toggle = event.target && event.target.closest
        ? event.target.closest('[data-wishlist-toggle]')
        : null;
      if (!toggle) return;

      if (toggleGate.has(toggle)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const gate = { state: pressedState(toggle), timer: 0, observer: null };
      gate.observer = new MutationObserver(() => {
        if (pressedState(toggle) !== gate.state) releaseGate(toggle);
      });
      gate.timer = setTimeout(() => releaseGate(toggle), TOGGLE_SETTLE_MS);
      toggleGate.set(toggle, gate);
      gate.observer.observe(toggle, { attributes: true, attributeFilter: ['class', 'aria-pressed'] });
    },
    true
  );

  const drawer = document.getElementById('WishlistDrawer');
  if (!drawer) return;

  const countEl = drawer.querySelector('[data-wishlist-count]');
  const shareBtn = drawer.querySelector('[data-wishlist-share]');
  const navCountEls = document.querySelectorAll('[data-wishlist-nav-count]');
  const addToCartLabel = drawer.dataset.addToCartLabel || '';
  const removeLabel = drawer.dataset.removeLabel || '';
  const WISHLIST_COUNT_STORAGE_KEY = 'atomic:wishlist-count';
  const WISHLIST_CUSTOMER_STORAGE_KEY = 'atomic:wishlist-customer';
  let observer = null;
  let hasRenderedWishlistCard = false;

  const getCachedWishlistCount = () => {
    try {
      const cachedCount = Number.parseInt(localStorage.getItem(WISHLIST_COUNT_STORAGE_KEY), 10);
      return Number.isFinite(cachedCount) && cachedCount >= 0 ? cachedCount : null;
    } catch {
      return null;
    }
  };

  const setCachedWishlistCount = (count) => {
    try {
      localStorage.setItem(WISHLIST_COUNT_STORAGE_KEY, String(count));
    } catch {
      return;
    }
  };

  const setWishlistNavVisible = (isVisible) => {
    document.querySelectorAll('[data-wishlist-nav-toggle]').forEach((toggle) => {
      toggle.classList.toggle('is-active', isVisible);
      toggle.toggleAttribute('hidden', !isVisible);
      toggle.setAttribute('aria-hidden', String(!isVisible));
    });

    // The share button is only meaningful when the wishlist has items —
    // an empty wishlist has no publicId to build a share URL from.
    if (shareBtn) {
      shareBtn.toggleAttribute('hidden', !isVisible);
    }
  };

  const setWishlistCountText = (count) => {
    if (countEl) {
      countEl.textContent = `(${count})`;
    }

    navCountEls.forEach((navCountEl) => {
      // Leave the badge empty (not "0") so the red dot collapses via :empty
      // and never renders a stray "0".
      navCountEl.textContent = count > 0 ? count : '';
    });
  };

  const hasEmptyWishlistState = () => {
    return !!drawer.querySelector(
      '.wk-empty, .wk-empty-state, .wk-empty-content, .wk-no-products, [data-wishlist-empty]'
    );
  };

  const applyCachedWishlistState = () => {
    const cachedCount = getCachedWishlistCount();
    if (cachedCount === null) return;

    setWishlistCountText(cachedCount);
    setWishlistNavVisible(cachedCount > 0);
  };

  // The cached count belongs to whichever wishlist session it was stored under.
  // When the logged-in customer changes — login, logout, or switching accounts —
  // that cache is stale and must be dropped so the badge doesn't leak the
  // previous user's count.
  const reconcileWishlistOwner = () => {
    try {
      const currentCustomerId = drawer.dataset.customerId || '';
      const storedCustomerId = localStorage.getItem(WISHLIST_CUSTOMER_STORAGE_KEY) || '';

      if (storedCustomerId === currentCustomerId) return;

      localStorage.removeItem(WISHLIST_COUNT_STORAGE_KEY);
      localStorage.setItem(WISHLIST_CUSTOMER_STORAGE_KEY, currentCustomerId);
      setWishlistCountText(0);
      setWishlistNavVisible(false);
    } catch {
      // localStorage unavailable — nothing to reconcile.
    }
  };

  const updateCount = () => {
    const count = drawer.querySelectorAll('wishlist-product-card').length;
    const cachedCount = getCachedWishlistCount();

    if (count > 0) {
      hasRenderedWishlistCard = true;
    }

    if (count === 0 && cachedCount > 0 && !hasRenderedWishlistCard && !hasEmptyWishlistState()) {
      setWishlistCountText(cachedCount);
      setWishlistNavVisible(true);
      return;
    }

    setWishlistCountText(count);
    setCachedWishlistCount(count);
    setWishlistNavVisible(count > 0);
  };

  const scheduleWishlistNavRefresh = () => {
    const optimisticCount = Math.max(getCachedWishlistCount() || 0, 1);

    setWishlistCountText(optimisticCount);
    setCachedWishlistCount(optimisticCount);
    setWishlistNavVisible(true);

    [250, 750, 1500].forEach((delay) => {
      setTimeout(updateCount, delay);
    });
  };

  const replaceTextNodesIn = (element) => {
    if (!element) return;

    element.childNodes.forEach((node) => {
      if (addToCartLabel && node.nodeType === Node.TEXT_NODE && /Add to Cart/.test(node.textContent)) {
        node.textContent = node.textContent.replace(/Add to Cart/g, addToCartLabel);
      }
    });
  };

  const fixCtaText = () => {
    drawer.querySelectorAll('.wk-cta-button').forEach((button) => {
      replaceTextNodesIn(button.querySelector('.wk-cta-label'));
      replaceTextNodesIn(button);
    });
  };

  const fixRemoveButtons = () => {
    drawer.querySelectorAll('remove-button .wk-button').forEach((button) => {
      if (!removeLabel) return;
      button.setAttribute('data-wishlist-remove-label', removeLabel);
    });
  };

  const stripCurrencyCode = (element) => {
    if (!element) return;

    element.childNodes.forEach((node) => {
      if (node.nodeType !== Node.TEXT_NODE) return;

      const cleaned = node.textContent.replace(/\s+[A-Z]{3}\b/g, '');
      if (cleaned !== node.textContent) {
        node.textContent = cleaned;
      }
    });
  };

  const fixPrices = () => {
    drawer.querySelectorAll('.wk-current-price, .wk-compare-price').forEach(stripCurrencyCode);
    drawer.querySelectorAll('.wk-price').forEach((price) => {
      const currentPrice = price.querySelector('.wk-current-price');
      const isOnSale = !!(
        currentPrice &&
        price.querySelector('.wk-compare-price')?.textContent.trim()
      );

      if (currentPrice) {
        currentPrice.classList.toggle('wk-on-sale', isOnSale);
        currentPrice.toggleAttribute('data-on-sale', isOnSale);
      }
    });
  };

  // ---------------------------------------------------------------------
  // Variant dropdowns — the shared atomic-dropdown component over Swish's
  // native select (AAS-1180, Figma 14401-190523/-190535/-190693). The
  // native <select class="wk-input"> stays in the DOM as the state carrier
  // — Swish's Lit bindings react to its bubbled 'change' — but is hidden by
  // CSS; the visible UI is the theme's shared trigger + option listbox
  // (assets/atomic-dropdown.js) at compact sizing (--atomic-dd-trigger-height
  // in wishlist-modal.css). Lit re-renders can replace card DOM wholesale,
  // so enhancement re-runs from the same MutationObserver that drives
  // refresh(); AtomicDropdown.enhance is idempotent.
  // ---------------------------------------------------------------------
  const enhanceOptionDropdowns = () => {
    drawer.querySelectorAll('.wk-option-select .wk-control select.wk-input').forEach((select) => {
      window.AtomicDropdown?.enhance(select);
    });
  };

  // ---------------------------------------------------------------------
  // AAS-4635 retail-only — some products stay visible with their price but
  // must not be purchasable, per market and per customer group.
  //
  // Every other surface gets its rules written into the markup by Liquid
  // (snippets/retail-only-data.liquid). The wishlist cannot: Swish renders the
  // cards client-side from its own product payload, which carries no
  // metafields. So the rules are fetched per handle from
  // templates/product.retail-only.liquid and handed to the SAME evaluator
  // every other surface uses. Nothing here knows what a rule means — no
  // market, no profile, no D2C-by-exclusion. An earlier attempt (d75264d)
  // re-implemented the rule model in Liquid; keeping two copies of it in step
  // is a standing correctness hazard and is deliberately not repeated.
  //
  // The feed is a pure function of the handle — it reads no market, no
  // customer, no request — so requesting it on the "wrong" URL cannot change
  // the answer. That is what deletes the bug behind a32b76e, where the lookup
  // resolved in the DEFAULT market (which on this shop is `international`,
  // where retail-only is switched off entirely) and therefore reported every
  // product as buyable.
  // ---------------------------------------------------------------------
  const RETAIL_ONLY_FEED = 'retail-only-rules-v1';
  // A correct feed response is ~100 bytes. The point of the cap is the
  // incorrect one: Shopify answers ?view=<template that does not exist> with
  // HTTP 200 and the whole product page (~700KB, chunked, with no
  // content-length to check), so the body is read through a bounded reader and
  // abandoned the moment it cannot be ours.
  const RETAIL_ONLY_MAX_BYTES = 8192;
  // A failed lookup is never cached as an answer, so without a cooldown the
  // background pass would re-request it on every Lit mutation — refresh() runs
  // from a MutationObserver. A customer's own submit ignores the cooldown:
  // that is one deliberate request, and it deserves a fresh attempt.
  const RETAIL_ONLY_RETRY_MS = 30000;
  const retailOnlyLabel = drawer.dataset.retailOnlyLabel || '';
  const retailOnlyRules = new Map();
  const retailOnlyFailures = new Map();
  let retailOnlyFeedBroken = false;
  let retailOnlyArmed = false;

  // The single switch for the whole feature. #retail-only-context is rendered
  // by snippets/retail-only-context.liquid from layout/theme.liquid, under the
  // same `unless … == 'international'` gate that renders this drawer — so on
  // `international` it is absent, and nothing below ever runs. That market is
  // already catalog-only via AAS-4164 and must not be touched. This signal is
  // part of the HTML document itself, so unlike a missing script it cannot be
  // absent because of a CDN failure or a blocked asset.
  const retailOnlyActive = () => !!document.getElementById('retail-only-context');

  // One-shot LOGGING only. It deliberately does not gate lookups: these are
  // per-response conditions (a redirect that drops the query string, one odd
  // handle), and latching the whole feature off the first of them would bounce
  // every product — normal ones included — to the PDP for the rest of the page
  // session. That is the "per item, never global" rule this ticket exists to
  // keep. Request volume is bounded by the per-handle cooldown instead.
  const feedMissing = (reason) => {
    if (!retailOnlyFeedBroken) {
      retailOnlyFeedBroken = true;
      console.error(
        `[wishlist] retail-only feed unusable (${reason}). Is templates/product.retail-only.liquid deployed?`
      );
    }
    return new Error(`retail-only feed: ${reason}`);
  };

  // Read at most RETAIL_ONLY_MAX_BYTES and give up as soon as the body cannot
  // be our feed. Without this a missing template downloads the full product
  // page once per wishlist item.
  const readBoundedBody = async (response) => {
    if (!response.body || typeof response.body.getReader !== 'function') {
      const whole = await response.text();
      if (whole.length > RETAIL_ONLY_MAX_BYTES) throw feedMissing('oversized body');
      return whole;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let text = '';

    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        text += decoder.decode(value, { stream: true });
        // An HTML page starts with `<`; our feed starts with `{`.
        if (text.trim() && text.trim()[0] !== '{') throw feedMissing('body is not JSON');
        if (text.length > RETAIL_ONLY_MAX_BYTES) throw feedMissing('oversized body');
      }
    } finally {
      reader.cancel().catch(() => {});
    }

    return text + decoder.decode();
  };

  // Prefix from Shopify.routes.root (market-prefixed — "/de-at/"), handle from
  // the card. a32b76e took the whole path from the card's own link, whose
  // prefixing belongs to Swish rather than to us. The answer no longer depends
  // on the prefix anyway; a wrong one can only 404, which blocks.
  const productPath = (handle) => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root.replace(/\/+$/, '')}/products/${encodeURIComponent(handle)}`;
  };

  const retailOnlyFeedUrl = (handle) => `${productPath(handle)}?view=retail-only`;

  const fetchRetailOnlyRules = (handle, force) => {
    if (retailOnlyRules.has(handle)) return retailOnlyRules.get(handle);
    if (!force) {
      const failedAt = retailOnlyFailures.get(handle);
      if (failedAt && Date.now() - failedAt < RETAIL_ONLY_RETRY_MS) {
        return Promise.reject(new Error('retail-only feed: cooling down'));
      }
    }

    // No Accept: application/json. Shopify content-negotiates product URLs and
    // that header returns the product payload instead of this template —
    // 200 OK, valid JSON, no retail_only data (ed8cc5f).
    const request = fetch(retailOnlyFeedUrl(handle), { credentials: 'same-origin' })
      .then(async (response) => {
        if (!response.ok) throw new Error(`retail-only feed ${response.status}`);

        const text = await readBoundedBody(response);
        let data = null;
        try {
          data = JSON.parse(text);
        } catch (error) {
          throw feedMissing('body is not JSON');
        }
        // The sentinel is what makes "the template is missing" and "this is
        // some other 200" decidable at all. Anything unexpected is a FAILED
        // LOOKUP, never a licence to buy.
        if (!data || data.feed !== RETAIL_ONLY_FEED || !Array.isArray(data.rules)) {
          throw feedMissing('unexpected payload');
        }
        // And the echo proves the rules belong to the product we asked about,
        // not to whatever else a redirect or a rewrite may have served.
        if (data.handle !== handle) {
          throw new Error(`retail-only feed: answered for ${data.handle}, asked for ${handle}`);
        }
        return data.rules;
      })
      .catch((error) => {
        // Never cache a failure as an answer.
        retailOnlyRules.delete(handle);
        retailOnlyFailures.set(handle, Date.now());
        throw error;
      });

    retailOnlyRules.set(handle, request);
    return request;
  };

  // Delegates to the one evaluator. The explicit throw matters: an optional
  // call would yield undefined, and `if (undefined)` reads as "not retail
  // only" — the same shape of bug as ed8cc5f's `!!undefined`.
  const isRetailOnly = (rules) => {
    const evaluator = window.RetailOnly;
    if (!evaluator || typeof evaluator.isRetailOnly !== 'function') {
      throw new Error('retail-only evaluator missing');
    }
    return evaluator.isRetailOnly(rules);
  };

  const productHandleFrom = (element) => {
    if (!element) return null;
    const href = element.querySelector('a[href*="/products/"]')?.getAttribute('href');
    const match = href && href.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  };

  // The custom element first, then the card wrapper, then the form's own
  // parent — so a Swish rename of the element does not silently cost us the
  // handle and turn the gate into a blanket block.
  const cardScopeFor = (form) =>
    form.closest('wishlist-product-card') || form.closest('.wk-product-card') || form.parentElement;

  // Hands the fetched rules to assets/retail-only.js and lets IT classify the
  // card, exactly as it classifies a PLP card carrying the same attribute:
  // it sets data-retail-only-state and the retail-only--active class, which is
  // what assets/wishlist-modal.css keys off. One evaluator, one class, one
  // state attribute across every surface.
  //
  // The marker goes on the <wishlist-product-card> HOST. wishlist-page.js
  // renders cards with a keyed repeat(), so the host survives re-renders while
  // its children are replaced — the CSS is descendant-scoped from the host and
  // therefore re-applies to new markup with no re-marking and no race.
  const classifyRetailOnlyCard = (card) => {
    if (card.hasAttribute('data-retail-only-state')) return;
    const handle = productHandleFrom(card);
    if (!handle) return;

    fetchRetailOnlyRules(handle).then(
      (rules) => {
        if (card.hasAttribute('data-retail-only-state')) return;
        // Set before the class arrives: retail-only.js adds the class inside
        // refresh(), and the CSS replaces the button label with this value.
        // The marker attribute is what the CSS keys the swap off, so a drawer
        // rendered without [data-retail-only-label] (a stale snippet, a partial
        // deploy) keeps the original CTA text — still disabled and inert —
        // rather than hiding the label and rendering an empty button.
        if (retailOnlyLabel) {
          card.style.setProperty('--wishlist-retail-only-label', JSON.stringify(retailOnlyLabel));
          card.setAttribute('data-retail-only-labelled', '');
        }
        card.setAttribute('data-retail-only-rules', JSON.stringify(rules));
        window.RetailOnly?.refresh(card);
        // Immediately, not only from the next refresh(): classification
        // resolves asynchronously and can land after the last Lit mutation,
        // in which case no further refresh() would ever fire for this card.
        if (card.classList.contains('retail-only--active')) defuseRetailOnlyCard(card);
      },
      () => {
        // Leave it unclassified so a later pass retries. The submit gate is
        // authoritative regardless of whether this ever succeeds.
      }
    );
  };

  // Lazy on purpose. The drawer is rendered on every page of the site, so
  // classifying on load would fire one feed request per wishlist item on every
  // pageview. Nothing needs an answer until the customer can see a card.
  const markRetailOnlyCards = () => {
    if (!retailOnlyArmed || !retailOnlyActive()) return;
    document.querySelectorAll('wishlist-product-card').forEach(classifyRetailOnlyCard);
  };

  // Strip the buying affordances from a card already proven retail-only. This
  // is cosmetics and defence in depth, NOT the block — the submit gate below
  // is what decides. Re-applied on every refresh because a Lit re-render
  // restores the original markup.
  const defuseRetailOnlyCard = (card) => {
    card.querySelectorAll('.wk-form[action]').forEach((form) => form.removeAttribute('action'));
    card.querySelectorAll('.wk-cta-button').forEach((button) => {
      button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-disabled', 'true');
      // The visible label is a CSS ::after, so give the button an explicit
      // accessible name rather than relying on pseudo-element content.
      if (retailOnlyLabel) button.setAttribute('aria-label', retailOnlyLabel);
    });
  };

  const defuseRetailOnlyCards = () => {
    document.querySelectorAll('wishlist-product-card.retail-only--active').forEach(defuseRetailOnlyCard);
  };

  const refresh = () => {
    updateCount();
    fixCtaText();
    fixRemoveButtons();
    fixPrices();
    enhanceOptionDropdowns();
    markRetailOnlyCards();
    defuseRetailOnlyCards();
  };

  // Arm on first open. info-drawer.js sets aria-hidden="false" to open.
  if (retailOnlyActive()) {
    const armRetailOnly = () => {
      if (drawer.getAttribute('aria-hidden') === 'false' && !retailOnlyArmed) {
        retailOnlyArmed = true;
        markRetailOnlyCards();
      }
    };
    new MutationObserver(armRetailOnly).observe(drawer, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
    armRetailOnly();
  }

  // Defence in depth, and the only guard that reaches surfaces this file does
  // not own — the /apps/wishlist shared page renders its cards outside the
  // drawer, where the submit handler below never runs. Deliberately narrow: it
  // acts ONLY on a card already PROVEN retail-only, so it can never touch a
  // normal product, whatever else changes. Capture phase at the document means
  // it runs before anything bound on the drawer, the card or the form.
  const blockProvenRetailOnly = (event) => {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('wishlist-product-card.retail-only--active')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  document.addEventListener('submit', blockProvenRetailOnly, true);
  document.addEventListener(
    'click',
    (event) => {
      if (!event.target?.closest?.('.wk-cta-button')) return;
      blockProvenRetailOnly(event);
    },
    true
  );

  // ---------------------------------------------------------------------
  // Share — the visible button is theme UI; the actual share (public URL
  // generation at /apps/wishlist/<publicId>) is Swish: Wishlist King's.
  // Primary path: the WishlistKing headless SDK. Fallback: forward the
  // click to Swish's own share control, rendered (hidden) inside the
  // drawer via the show-share-button attribute on <wishlist-page>.
  // ---------------------------------------------------------------------
  let shareCopiedTimer = null;

  const flashCopied = () => {
    if (!shareBtn) return;
    shareBtn.classList.add('is-copied');
    clearTimeout(shareCopiedTimer);
    shareCopiedTimer = setTimeout(() => shareBtn.classList.remove('is-copied'), 2000);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      try {
        // execCommand fallback for older browsers / non-secure contexts.
        // select() moves focus to the helper textarea, so restore it —
        // losing focus mid-drawer breaks keyboard navigation.
        const previouslyFocused = document.activeElement;
        const helper = document.createElement('textarea');
        helper.value = text;
        helper.setAttribute('readonly', '');
        helper.setAttribute('aria-hidden', 'true');
        helper.style.position = 'fixed';
        helper.style.opacity = '0';
        document.body.appendChild(helper);
        helper.select();
        const copied = document.execCommand('copy');
        helper.remove();
        if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
          previouslyFocused.focus();
        }
        return copied;
      } catch {
        return false;
      }
    }
  };

  // Resolves the wishlist's shareable publicId, then lets Swish share it.
  // shareWishlist() natively tries the Web Share sheet first, then the
  // clipboard, and reports which one succeeded.
  // Returns 'webShare' | 'clipboard' | false.
  const shareViaSdk = async () => {
    const wk = window.WishlistKing;
    if (!wk || typeof wk.shareWishlist !== 'function') return false;

    let publicId = null;
    try {
      const api = wk._apis && wk._apis.wishlist;
      if (api && typeof api.loadWishlist === 'function') {
        const response = await api.loadWishlist({ wishlistId: 'mine' });
        publicId = response && response.wishlist && response.wishlist.publicId;
      }
    } catch {
      publicId = null;
    }
    if (!publicId) return false;

    try {
      const result = await wk.shareWishlist({ wishlistId: publicId });
      if (result && result.webShare) return 'webShare';
      if (result && result.clipboard) return 'clipboard';
    } catch {
      // fall through to the manual copy below
    }

    const base = (wk.routes && wk.routes.wishlistUrl) || '/apps/wishlist';
    const copied = await copyToClipboard(`${window.location.origin}${base}/${publicId}`);
    return copied ? 'clipboard' : false;
  };

  const shareViaNativeButton = () => {
    const nativeShare = drawer.querySelector('wishlist-share button, wishlist-share .wk-button');
    if (!nativeShare) return false;
    nativeShare.click();
    return true;
  };

  if (shareBtn) {
    let shareInFlight = false;

    const attemptShare = async () => {
      const sdkResult = await shareViaSdk();
      if (sdkResult) {
        // Only announce "Link copied" for clipboard shares — the Web Share
        // sheet is its own confirmation.
        if (sdkResult === 'clipboard') flashCopied();
        return true;
      }

      if (shareViaNativeButton()) {
        flashCopied();
        return true;
      }

      return false;
    };

    shareBtn.addEventListener('click', async () => {
      if (shareInFlight) return;
      shareInFlight = true;

      try {
        if (await attemptShare()) return;

        // Swish hydrates lazily — if neither path is ready yet, retry
        // briefly (~2.5s) before giving up.
        for (let attempt = 0; attempt < 10; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, 250));
          if (await attemptShare()) return;
        }
      } finally {
        shareInFlight = false;
      }
    });
  }

  reconcileWishlistOwner();
  applyCachedWishlistState();

  document.addEventListener(
    'click',
    (event) => {
      const wishlistToggle = event.target.closest('[data-wishlist-toggle][data-product-handle]');
      if (!wishlistToggle) return;

      const wasOn = wishlistToggle.classList.contains('is-wishlisted') ||
        wishlistToggle.getAttribute('aria-pressed') === 'true';
      if (wasOn) return;

      scheduleWishlistNavRefresh();
    },
    true
  );

  const startObserving = () => {
    const page = drawer.querySelector('wishlist-page');
    if (!page || observer) return;

    observer = new MutationObserver(refresh);
    observer.observe(page, { childList: true, subtree: true });
    refresh();
  };

  let tries = 0;
  const poll = setInterval(() => {
    startObserving();
    tries += 1;

    if (observer || tries > 40) {
      clearInterval(poll);
    }
  }, 250);

  const closeWishlistDrawer = () => {
    const closeBtn = drawer.querySelector('[data-drawer-close]');
    if (closeBtn) {
      closeBtn.click();
      return;
    }

    drawer.removeAttribute('open');
    drawer.classList.remove('is-open');
  };

  // This handler IS the drawer's add-to-cart. It pre-empts Swish's own action
  // (it is bound on an ancestor in the capture phase and stops propagation,
  // while Swish delegates from the card in the bubble phase), so the app's
  // add-to-cart.js never runs here — which makes this the one place the
  // retail-only decision has to be made, and the only place it can be made
  // authoritatively.
  drawer.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest('.wk-form');
      if (!form) return;

      event.preventDefault();
      event.stopPropagation();

      // AAS-4635 — resolve before adding. This gate, not the hidden button, is
      // what blocks: classification is async and lazy, so a submit can land on
      // a card that has not been classified yet. It is placed here rather than
      // on the event because the add is performed BELOW this point — there is
      // no path to /cart/add.js that does not pass through here, and normal
      // products keep the exact code path they have today.
      if (retailOnlyActive()) {
        const handle = productHandleFrom(cardScopeFor(form));

        if (!handle) {
          // Cannot identify the product, so cannot establish that it is safe
          // to buy. The add is irreversible from the customer's side; a failed
          // identification is not a licence.
          console.warn('[wishlist] retail-only: no product handle — add blocked');
          return;
        }

        // .catch() rather than .then(onOk, onErr): the rejection handler has to
        // cover isRetailOnly() throwing too — it does throw when the evaluator
        // is missing — and a sibling onErr cannot catch a throw from its own
        // onOk. Getting that wrong would turn "the evaluator failed to load"
        // into a silently dead button on EVERY wishlist add, retail-only or
        // not. The add itself is deliberately outside the catch, so a failure
        // inside it can never be mistaken for an unresolved lookup.
        fetchRetailOnlyRules(handle, true)
          .then((rules) => isRetailOnly(rules))
          .catch(() => {
            // Unresolvable: a network error, a missing evaluator, or the feed
            // is not deployed. Do not add, and do not leave the customer with
            // a dead button — send them to the PDP, which is correct either
            // way. A normal product is buyable there; a retail-only one shows
            // its "Not available at atomic.com" state and the find-in-store
            // CTA.
            console.warn('[wishlist] retail-only lookup failed — routing to the product page');
            window.location = productPath(handle);
            return true;
          })
          .then((blocked) => {
            if (!blocked) addToCartFromWishlist(form);
          });
        return;
      }

      addToCartFromWishlist(form);
    },
    true
  );

  function addToCartFromWishlist(form) {
      const cartDrawer = document.querySelector('cart-drawer');
      const hasCartDrawer = cartDrawer && typeof cartDrawer.renderContents === 'function';

      const formData = new FormData(form);

      if (hasCartDrawer) {
        try {
          if (typeof cartDrawer.getSectionsToRender === 'function') {
            formData.append('sections', cartDrawer.getSectionsToRender().map((section) => section.id).join(','));
            formData.append('sections_url', typeof getSectionsUrl === 'function' ? getSectionsUrl() : window.location.pathname);
          }
        } catch {
          formData.delete('sections');
          formData.delete('sections_url');
        }
      }

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) return;

          if (hasCartDrawer) {
            cartDrawer.renderContents(response);
            closeWishlistDrawer();
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch((error) => {
          console.error('[wishlist] add to cart failed:', error);
        });
  }
})();
